'use client'
import { useEffect, useRef } from 'react'
import useUser from 'auth/hooks/useUser'
import type { ProductLightDTO } from 'ecosystem'
import { fetchJson } from 'api'
import { useDispatch, useSelector } from 'shared-redux'
import { refreshCartProducts, selectedCartItems } from 'shared-redux/state'

const REFRESH_PATH = '/api/cart/refresh-prices'

const collectCartProductIds = (cartItems: ReturnType<typeof selectedCartItems>): string[] => {
  const ids = new Set<string>()
  cartItems.forEach((item) => {
    if (item.type === 'SINGLE') {
      ids.add(item.meta.singleType.product.id)
      item.meta.singleType.subItems?.forEach((sub) => ids.add(sub.product.id))
    } else {
      item.meta.groupType.items.forEach((detail) => {
        ids.add(detail.product.id)
        detail.subItems?.forEach((sub) => ids.add(sub.product.id))
      })
    }
  })
  return [...ids]
}

/**
 * Mount once near the root of the app. Watches the logged-in customer id and
 * fires a best-effort cart-price refresh whenever it flips (login, logout,
 * session expiry → user becomes null). The refresh is silent: errors and
 * missing items are no-ops, so the cart stays usable even when the BE is
 * unreachable. BE is the gatekeeper at order-submit.
 */
export const useCartAuthSync = () => {
  const { user, isLoading } = useUser()
  const cartItems = useSelector(selectedCartItems)
  const dispatch = useDispatch()

  const customerId = user?.customer?.id ?? null
  const prevCustomerId = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (isLoading) return
    // First settled read just records the baseline — no refresh on mount.
    if (prevCustomerId.current === undefined) {
      prevCustomerId.current = customerId
      return
    }
    if (prevCustomerId.current === customerId) return
    prevCustomerId.current = customerId

    const productIds = collectCartProductIds(cartItems)
    if (!productIds.length) return

    let aborted = false
    fetchJson<ProductLightDTO[]>(REFRESH_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds })
    })
      .then((products) => {
        if (aborted || !products?.length) return
        dispatch(refreshCartProducts(products))
      })
      .catch(() => void 0)

    return () => {
      aborted = true
    }
    // We intentionally don't depend on `cartItems` — adding/removing items
    // shouldn't fire a price refresh, only auth state changes should.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- need
  }, [customerId, isLoading])
}
